// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    handleNotificationError,
    mergeTag,
    onOrganisationImageUpload,
    openMessage,
    trimTags,
    useLocalMessage,
} from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import client from '@Client';
import useOneQuery from '@Hooks/useOneQuery';
import { AlertOutput, AlertStatusStage, QuerySelector, CommentOutput, PermissionsEnum } from 'thehive-sdk';
import { THLayout, useStorageCollapse } from '@Molecules/THLayout';
import { RouteType } from '@Types';
import { computeEventList } from '@Organisms/Alert/eventList';
import { THForm } from '@Molecules/THForm/THForm';
import { Form } from 'antd';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { useCurrentUser } from '@Queries/userQueries';
import { CFList } from '@Molecules/CFList/CFList';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import AlertDescriptionsSider from '@Organisms/Alert/AlertDescriptionSider/AlertDescriptionSider';
import { computeGetSelector } from '@Organisms/Alert/selector';
import { ICONS } from '@Constants/iconsApp';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { mergeCF } from '@Helpers/customField';
import { ScopeContext } from '@Context/ScopeProvider';
import useListQuery from '@Hooks/useListQuery';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './AlertDescription.scss';

const refItemsPerPage = 20;

const computeSelectorComment = (alertId: string): QuerySelector => ({
    head: [{ name: 'getAlert', idOrName: alertId }, { name: 'comments' }],
    name: `get-alert-comments-${alertId}`,
    sort: ['-_createdAt'],
});

const AlertDescription = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { alertId } = useParams<{ alertId: string }>();
    const [form] = Form.useForm();
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const [itemsPerPage, setItemsPerPage] = useState(refItemsPerPage);
    const [currentData, setCurrentData] = useState<AlertOutput>();
    const [updatedData, setUpdatedData] = useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);
    const [activeKeyCF, setActiveKeyCF] = useState('0');
    const [editMode, setEditMode] = React.useState(false);

    const {
        data: alertData,
        error,
        refetch,
    } = useOneQuery<AlertOutput>({
        selector: computeGetSelector({ alertId }),
        controller: client.AlertController,
    });

    const { data: dataComment = [], refetch: listRefetchComment } = useListQuery<CommentOutput[]>({
        controller: client.CommentController,
        queryParams: {
            paginationOptions: { page: 0, itemsPerPage },
            selector: computeSelectorComment(alertId),
            sort: ['-_createdAt'],
        },
    });

    const { status = '', stage = '' } = currentData || {};

    const { customFieldList } = useContext(ScopeContext);

    const {
        collapse: { opened },
    } = useStorageCollapse();

    useEffect(() => {
        if (error) {
            openMessage('error', i('notification.alert.doesnotExist'));
            history.push(RouteBuilder.compute('alerts.default'));
        }
    }, [error, history, i]);

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.AlertController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    setForceUpdate(true);
                    if (message) {
                        openMessage('success', message);
                    }
                    refetch();
                },
                history,
            }),
        [history, i, refetch],
    );

    const hasPermissions = useSplitPermissions(permissions, [PermissionsEnum.MANAGE_ALERT_UPDATE]);

    const editAlert = useCallback(
        ({ customFields, tags, ...values }) => {
            const treatedCF = customFields
                ? mergeCF(currentData?.customFields || [], alertData?.customFields || [], customFields)
                : [];

            const treatedTag = trimTags(tags ? mergeTag(currentData?.tags || [], alertData?.tags || [], tags) : []);

            eventList.onEdit(
                {
                    ...values,
                    ...(customFields ? { customFields: treatedCF } : {}),
                    ...(tags ? { tags: treatedTag } : {}),
                },
                alertId,
                stage === AlertStatusStage.NEW,
            );

            setEditMode(false);
        },
        [alertData, alertId, currentData, eventList, stage],
    );

    useStreamObserver({ objectType: STREAM_EVENTS.COMMENT }, (data): void => {
        const canRefetch = data.some((action) => action.base.rootId === alertId);
        if (canRefetch) {
            listRefetchComment();
        }
    });

    useStreamObserver(
        { rootId: alertId, objectType: STREAM_EVENTS.ALERT },
        (data) => {
            const canRefetch = data.some(
                (audit) => audit.base.action === 'update' && currentUser?.login !== audit.base._createdBy,
            );

            if (canRefetch) {
                refetch();
            }
        },
        [editMode, currentUser?.login],
    );

    useEffect(() => {
        form.setFieldsValue(currentData);
        form.resetFields();
    }, [currentData]); // eslint-disable-line

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(alertData);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode]);

    useEffect(() => {
        if ((!editMode && alertData) || forceUpdate) {
            setCurrentData(alertData);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [alertData, editMode, forceUpdate]); // eslint-disable-line

    return (
        <THLayout>
            <THLayout.Content>
                <THForm<AlertOutput>
                    name="alert-description-form"
                    form={form}
                    editable
                    initialValues={{
                        ...currentData,
                        customFields: currentData?.customFields.sort(({ value: valueA }, { value: valueB }) =>
                            `${valueA}`.localeCompare(`${valueB}`),
                        ),
                    }}
                    className="alert-description__form"
                    onFinish={editAlert}
                    submitTouchedValues={['customFields']}
                    onValuesChange={() => {
                        setEditMode(true);
                    }}
                >
                    <div className="alert-description__body">
                        <div className="alert-description__body__content">
                            <THForm.Item label={i('common.tags')} name="tags">
                                <THTagSelector.Autocomplete borderless disabled={!hasPermissions.updateAlert} />
                            </THForm.Item>

                            <THForm.Item label={i('common.description')} name="description">
                                <THMarkdown
                                    borderless
                                    disabled={!hasPermissions.updateAlert}
                                    onImageUpload={onOrganisationImageUpload}
                                    formEditMode={editMode}
                                />
                            </THForm.Item>

                            {status && (
                                <div className="alert-description__section">
                                    <THForm.Item name="summary" label={i('common.summary')}>
                                        <THMarkdown
                                            borderless
                                            disabled={!hasPermissions.updateAlert}
                                            onImageUpload={onOrganisationImageUpload}
                                            formEditMode={editMode}
                                        />
                                    </THForm.Item>
                                </div>
                            )}

                            <THForm.Item name="customFields">
                                <CFList
                                    form={form}
                                    customFields={customFieldList}
                                    borderless
                                    disabled={!hasPermissions.updateAlert}
                                    isDeletable={false}
                                    activeKeyState={{
                                        activeKey: activeKeyCF,
                                        setActiveKey: setActiveKeyCF,
                                    }}
                                />
                            </THForm.Item>
                        </div>
                    </div>

                    <div className="alert-description__footer">
                        <FormSubmitButton
                            warning
                            formName="alert-description-form"
                            disabled={false}
                            onCancel={() => {
                                form.resetFields();
                                setEditMode(false);
                            }}
                            hidden={!editMode}
                        />
                    </div>
                </THForm>
            </THLayout.Content>
            <THLayout.Sider
                initialCollapse={opened.includes('alert-comment')}
                className="alert-description__wrapper"
                name="alert-comment"
            >
                <AlertDescriptionsSider
                    data={alertData}
                    dataComment={dataComment}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                />
            </THLayout.Sider>
        </THLayout>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'general.general',
        icon: <ICONS.AlertIcon />,
    },
    path: RouteBuilder.compute('alerts.details'),
    content: <AlertDescription />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.details' },
};
